import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Heart, Lock, ArrowLeft, Camera, CalendarDays, Stethoscope, CalendarHeart } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { API, resolvePhoto } from "../components/person/shared";
import {
  OverviewSection,
  JourneySection,
  PatientNotesSection,
  PriorityRequestsSection,
  UpdatesFeed,
  CareTeamSection,
} from "../components/person/JourneySections";
import {
  NeedsSection,
  MessagesSection,
  QuestionsSection,
  ChurchSection,
  PageFooter,
} from "../components/person/HelpSections";

const PersonPage = ({ forcedSlug }) => {
  const params = useParams();
  const slug = forcedSlug || params.slug;
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [exists, setExists] = useState(false);
  const [gateName, setGateName] = useState("");
  const [gateTheme, setGateTheme] = useState(null);
  const [gatePhoto, setGatePhoto] = useState(null);
  const [gateTitle, setGateTitle] = useState(null);
  const [data, setData] = useState(null);
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [posting, setPosting] = useState(false);
  const [praying, setPraying] = useState(false);
  const [prayed, setPrayed] = useState(false);
  const [noteName, setNoteName] = useState("");
  const [noteText, setNoteText] = useState("");
  const [noteSending, setNoteSending] = useState(false);
  const [noteSent, setNoteSent] = useState(false);
  const [photoName, setPhotoName] = useState("");
  const [photoCaption, setPhotoCaption] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const storedPasscode = useCallback(() => sessionStorage.getItem(`hart_pc_${slug}`) || "", [slug]);

  const theme = data?.person?.theme || gateTheme;
  const themeClass = theme === "navy" ? "theme-navy" : "";

  const verify = useCallback(
    async (code, { silent } = {}) => {
      setVerifying(true);
      try {
        const res = await axios.post(`${API}/persons/${slug}/verify`, { passcode: code });
        setData(res.data);
        sessionStorage.setItem(`hart_pc_${slug}`, code);
        setError(false);
      } catch (e) {
        sessionStorage.removeItem(`hart_pc_${slug}`);
        if (!silent) setError(true);
      } finally {
        setVerifying(false);
      }
    },
    [slug]
  );

  useEffect(() => {
    setPrayed(localStorage.getItem(`hart_prayed_${slug}`) === "1");
    let active = true;
    const init = async () => {
      try {
        const res = await axios.get(`${API}/persons/${slug}/gate`);
        if (!active) return;
        setExists(res.data.exists);
        setGateName(res.data.fullName || "");
        setGateTheme(res.data.theme || null);
        setGatePhoto(res.data.photo || null);
        setGateTitle(res.data.heroTitle || null);
        if (res.data.exists && storedPasscode()) {
          await verify(storedPasscode(), { silent: true });
        }
      } catch (e) {
        if (active) setExists(false);
      } finally {
        if (active) setLoading(false);
      }
    };
    init();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (loading) {
    return (
      <main className={`min-h-screen bg-gradient-warm flex items-center justify-center ${themeClass}`}>
        <Heart className="h-8 w-8 text-primary animate-pulse" />
      </main>
    );
  }

  if (!exists) {
    return (
      <main className="min-h-screen bg-gradient-warm flex items-center justify-center px-6">
        <div className="text-center animate-fade-up">
          <Heart className="h-8 w-8 text-primary mx-auto" />
          <h1 className="mt-6 font-serif text-3xl text-foreground">We couldn't find that page.</h1>
          <p className="mt-3 text-sm text-muted-foreground max-w-sm mx-auto">
            Double-check the name shared with you by the family — it usually looks like{" "}
            <span className="text-foreground">ann-fisher</span>.
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-medium hover:bg-primary/90 transition-gentle"
          >
            <ArrowLeft className="h-4 w-4" /> Return home
          </Link>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className={`min-h-screen bg-gradient-warm flex items-center justify-center px-6 ${themeClass}`}>
        <div className="w-full max-w-md animate-fade-up">
          <div className="rounded-3xl bg-card shadow-card p-6 sm:p-8 text-center">
            {gatePhoto && (
              <img
                src={resolvePhoto(gatePhoto)}
                alt={gateName}
                data-testid="gate-photo"
                className="w-full max-h-80 rounded-2xl object-cover object-top mb-6 shadow-card"
              />
            )}
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-soft">
              <Lock className="h-5 w-5 text-primary" />
            </div>
            <h1 className="mt-5 font-serif text-3xl text-foreground">{gateTitle || `${gateName}'s page`}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              This space is private. Enter the passcode shared by the family.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (passcode.trim()) verify(passcode.trim());
              }}
              className="mt-6"
            >
              <input
                type="password"
                value={passcode}
                data-testid="passcode-input"
                onChange={(e) => {
                  setPasscode(e.target.value);
                  setError(false);
                }}
                placeholder="Passcode"
                className="w-full h-12 rounded-2xl bg-secondary/60 border-0 px-4 text-center text-lg tracking-[0.4em] placeholder:tracking-normal placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              />
              {error && (
                <p className="mt-2 text-xs text-destructive" data-testid="passcode-error">
                  That passcode doesn't match. Please try again.
                </p>
              )}
              <button
                type="submit"
                disabled={verifying}
                data-testid="passcode-submit"
                className="mt-4 w-full rounded-full bg-gradient-rose text-primary-foreground px-6 py-3 text-sm font-medium shadow-soft hover:shadow-glow transition-gentle disabled:opacity-60"
              >
                {verifying ? "Checking\u2026" : "Enter"}
              </button>
            </form>
            {slug === "ann-fisher" && (
              <p className="mt-4 text-[11px] text-muted-foreground">Demo passcode: 1234</p>
            )}
          </div>
          <a
            href="https://www.openhart.us"
            data-testid="gate-hart-link"
            className="mt-6 flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-gentle"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to OpenHART
          </a>
        </div>
      </main>
    );
  }

  const { person, updates, needs, photos, milestones, messages } = data;

  const handlePray = async () => {
    if (prayed || praying) return;
    setPraying(true);
    try {
      const res = await axios.post(`${API}/persons/${slug}/pray`, { passcode: storedPasscode() });
      setData((prev) => ({ ...prev, prayerCount: res.data.prayerCount }));
      setPrayed(true);
      localStorage.setItem(`hart_prayed_${slug}`, "1");
      toast({ title: "Thank you", description: `Your prayer means the world to ${person.firstName} and the family.` });
    } catch (e) {
      toast({ title: "Couldn't send that", description: "Please try again." });
    } finally {
      setPraying(false);
    }
  };

  const handleSendPrayerNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setNoteSending(true);
    try {
      const res = await axios.post(`${API}/persons/${slug}/prayer-notes`, {
        passcode: storedPasscode(),
        name: noteName.trim(),
        note: noteText.trim(),
      });
      setData((prev) => ({ ...prev, prayerNotes: [res.data, ...(prev.prayerNotes || [])] }));
      setNoteSent(true);
      setNoteText("");
      toast({ title: "Prayer sent", description: `It now lives on ${person.firstName}'s page.` });
    } catch (err) {
      toast({ title: "Couldn't send", description: "Please try again." });
    } finally {
      setNoteSending(false);
    }
  };

  const handleClaimNeed = async (id) => {
    const name = window.prompt("Your name (so the family knows who's helping):");
    if (!name || !name.trim()) return;
    try {
      const res = await axios.post(`${API}/persons/${slug}/needs/${id}/claim`, {
        passcode: storedPasscode(),
        name: name.trim(),
      });
      setData((prev) => ({ ...prev, needs: prev.needs.map((n) => (n.id === id ? res.data : n)) }));
      toast({ title: "Thank you", description: "The family will see that you've got this covered." });
    } catch (e) {
      toast({
        title: "Couldn't claim this",
        description: e.response?.status === 409 ? "Someone just picked this up." : "Please try again.",
      });
    }
  };

  const handleClaimPriority = async (id) => {
    const name = window.prompt("Your name (so the family knows who's helping):");
    if (!name || !name.trim()) return;
    try {
      const res = await axios.post(`${API}/persons/${slug}/priority-requests/${id}/claim`, {
        passcode: storedPasscode(),
        name: name.trim(),
      });
      setData((prev) => ({
        ...prev,
        priorityRequests: prev.priorityRequests.map((r) => (r.id === id ? res.data : r)),
      }));
      toast({ title: "Thank you", description: `${person.firstName} will know you're on it.` });
    } catch (e) {
      toast({
        title: "Couldn't claim this",
        description: e.response?.status === 409 ? "Someone just picked this up." : "Please try again.",
      });
    }
  };

  const handlePostMessage = async ({ name, body, file }) => {
    setPosting(true);
    try {
      let photoUrl = null;
      if (file) {
        const fd = new FormData();
        fd.append("passcode", storedPasscode());
        fd.append("file", file);
        const up = await axios.post(`${API}/persons/${slug}/upload-image`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        photoUrl = up.data.url;
      }
      const res = await axios.post(`${API}/persons/${slug}/messages`, {
        passcode: storedPasscode(),
        name,
        body,
        photoUrl,
      });
      setData((prev) => ({ ...prev, messages: [res.data, ...prev.messages] }));
      toast({ title: "Message sent", description: `${person.firstName} and the family will love this.` });
      return true;
    } catch (err) {
      toast({ title: "Couldn't send", description: "Please try again." });
      return false;
    } finally {
      setPosting(false);
    }
  };

  const handlePostQuestion = async (form) => {
    setPosting(true);
    try {
      await axios.post(`${API}/persons/${slug}/questions`, { passcode: storedPasscode(), ...form });
      toast({ title: "Sent to the family", description: "Someone will get back to you when there's a quiet moment." });
      return true;
    } catch (err) {
      toast({ title: "Couldn't send", description: "Please try again." });
      return false;
    } finally {
      setPosting(false);
    }
  };

  const handlePostChurchMessage = async ({ name, body }) => {
    setPosting(true);
    try {
      const res = await axios.post(`${API}/persons/${slug}/church-messages`, {
        passcode: storedPasscode(),
        name,
        body,
      });
      setData((prev) => ({ ...prev, churchMessages: [res.data, ...(prev.churchMessages || [])] }));
      toast({ title: "Message posted", description: "The church family will see it here." });
      return true;
    } catch (err) {
      toast({ title: "Couldn't post", description: "Please try again." });
      return false;
    } finally {
      setPosting(false);
    }
  };

  const handleUploadWallPhoto = async (e) => {
    e.preventDefault();
    if (!photoFile) {
      toast({ title: "Pick a photo first", description: "Choose an image from your device." });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("passcode", storedPasscode());
      fd.append("name", photoName.trim());
      fd.append("caption", photoCaption.trim());
      fd.append("file", photoFile);
      const res = await axios.post(`${API}/persons/${slug}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setData((prev) => ({ ...prev, photos: [res.data, ...prev.photos] }));
      setPhotoFile(null);
      setPhotoName("");
      setPhotoCaption("");
      toast({ title: "Photo added", description: "It's now on the photo wall." });
    } catch (err) {
      toast({ title: "Couldn't upload", description: "Please try a different photo." });
    } finally {
      setUploading(false);
    }
  };

  return (
    <main className={`min-h-screen bg-gradient-warm ${themeClass}`}>
      {/* Hero */}
      <section className="mx-auto max-w-3xl px-6 pt-14 pb-12 text-center animate-fade-up">
        <Link
          to="/home"
          data-testid="hart-home-link"
          className="inline-flex items-center gap-1.5 rounded-full bg-card/80 backdrop-blur px-4 py-1.5 text-xs font-medium text-primary shadow-card hover:shadow-soft transition-gentle"
        >
          <Heart className="h-3.5 w-3.5 fill-primary" /> OpenHART
        </Link>
        <div className="mt-8">
          {person.photo ? (
            <img
              src={resolvePhoto(person.photo)}
              alt={person.fullName}
              className="w-full max-w-sm max-h-[420px] rounded-3xl object-cover object-top mx-auto ring-4 ring-card shadow-soft"
            />
          ) : (
            <div className="h-28 w-28 rounded-full mx-auto ring-4 ring-card shadow-soft bg-primary-soft flex items-center justify-center">
              <span className="font-serif text-4xl text-primary">{person.firstName?.[0]}</span>
            </div>
          )}
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl text-foreground leading-tight" data-testid="person-name">
            {person.heroTitle || `${person.fullName}, held by all of us.`}
          </h1>
          {person.verse && (
            <p className="mx-auto mt-4 max-w-xl font-serif italic text-base sm:text-lg text-primary leading-relaxed">
              "{person.verse}"
              {person.verseRef && (
                <span className="ml-2 not-italic text-xs text-muted-foreground">— {person.verseRef}</span>
              )}
            </p>
          )}
          <p className="mx-auto mt-5 max-w-xl text-sm sm:text-base text-foreground/80 leading-relaxed">
            {person.heroSubtitle || person.story}
          </p>
          {(person.surgeonName || person.surgeryDate) && (
            <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
              {person.surgeonName && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-card/80 backdrop-blur px-4 py-1.5 text-xs text-foreground/80 shadow-card">
                  <Stethoscope className="h-3.5 w-3.5 text-primary" /> Doctor · {person.surgeonName}
                </span>
              )}
              {person.surgeryDate && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-card/80 backdrop-blur px-4 py-1.5 text-xs text-foreground/80 shadow-card">
                  <CalendarHeart className="h-3.5 w-3.5 text-primary" /> Surgery · {person.surgeryDate}
                </span>
              )}
            </div>
          )}
          <button
            onClick={handlePray}
            disabled={prayed || praying}
            data-testid="pray-button"
            className={`mt-7 inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-medium transition-gentle ${
              prayed
                ? "bg-primary-soft text-primary cursor-default"
                : "bg-gradient-rose text-primary-foreground shadow-soft hover:shadow-glow"
            }`}
          >
            <Heart className={`h-4 w-4 ${prayed ? "fill-primary" : "fill-primary-foreground"}`} />
            {prayed ? `You're praying for ${person.firstName}` : `Praying for ${person.firstName}`}
          </button>
          {data.prayerCount > 0 && (
            <p className="mt-2.5 text-xs text-muted-foreground" data-testid="prayer-count">
              {data.prayerCount} {data.prayerCount === 1 ? "person is" : "people are"} praying for {person.firstName}
            </p>
          )}
          {prayed && !noteSent && (
            <form
              onSubmit={handleSendPrayerNote}
              className="mt-5 mx-auto max-w-md rounded-3xl bg-card shadow-card p-5 text-left animate-fade-up"
              data-testid="prayer-note-form"
            >
              <p className="text-xs uppercase tracking-[0.18em] text-primary mb-3">
                Add a short prayer or verse (optional)
              </p>
              <input
                value={noteName}
                onChange={(e) => setNoteName(e.target.value)}
                placeholder="Your name (optional)"
                data-testid="prayer-note-name"
                className="w-full h-10 rounded-2xl bg-secondary/60 border-0 px-4 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              />
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                maxLength={500}
                placeholder={`A prayer, a verse, a few words for ${person.firstName}\u2026`}
                data-testid="prayer-note-text"
                className="mt-2 w-full rounded-2xl bg-secondary/60 border-0 px-4 py-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 min-h-[70px] resize-none"
              />
              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setNoteSent(true)}
                  className="rounded-full bg-secondary text-foreground px-4 py-1.5 text-xs font-medium hover:bg-secondary/70 transition-gentle"
                >
                  Skip
                </button>
                <button
                  type="submit"
                  disabled={noteSending || !noteText.trim()}
                  data-testid="prayer-note-submit"
                  className="rounded-full bg-primary text-primary-foreground px-4 py-1.5 text-xs font-medium hover:bg-primary/90 transition-gentle disabled:opacity-50"
                >
                  {noteSending ? "Sending\u2026" : "Send prayer"}
                </button>
              </div>
            </form>
          )}
          {data.prayerNotes?.length > 0 && (
            <div className="mt-6 mx-auto max-w-md space-y-3 text-left" data-testid="prayer-notes-list">
              {data.prayerNotes.slice(0, 5).map((n) => (
                <div key={n.id} className="rounded-3xl bg-card/60 backdrop-blur px-5 py-4 shadow-card">
                  <p className="font-serif italic text-sm text-foreground/85 leading-relaxed">"{n.note}"</p>
                  <p className="mt-1.5 text-[11px] uppercase tracking-[0.18em] text-primary">{n.name}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="mx-auto max-w-3xl px-6 pb-16 space-y-14">
        <OverviewSection person={person} />
        <JourneySection person={person} />
        <PatientNotesSection person={person} patientNotes={data.patientNotes} />
        <PriorityRequestsSection person={person} priorityRequests={data.priorityRequests} onClaim={handleClaimPriority} />
        <UpdatesFeed person={person} updates={updates} />
        <CareTeamSection person={person} />
        <NeedsSection person={person} needs={needs} onClaim={handleClaimNeed} />
        <MessagesSection person={person} messages={messages} onPost={handlePostMessage} posting={posting} />
        <QuestionsSection person={person} onPost={handlePostQuestion} posting={posting} />

        {/* Photo wall */}
        <section>
          <div className="mb-6">
            <div className="text-xs uppercase tracking-[0.25em] text-primary">Memories</div>
            <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-foreground">Photo wall for {person.firstName}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Share a picture — old, new, silly, sweet. Anything that makes {person.firstName} smile.
            </p>
          </div>
          <form onSubmit={handleUploadWallPhoto} className="rounded-3xl bg-card shadow-card p-5 sm:p-6 space-y-3 mb-6">
            <label
              className={`flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-4 py-6 text-sm cursor-pointer transition-gentle ${
                photoFile
                  ? "border-primary bg-primary-soft/40 text-primary"
                  : "border-border text-muted-foreground hover:border-primary/40"
              }`}
            >
              <Camera className="h-4 w-4" />
              {photoFile ? photoFile.name : "Add a photo"}
              <input
                type="file"
                accept="image/*"
                data-testid="photo-file-input"
                className="hidden"
                onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
              />
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                value={photoName}
                onChange={(e) => setPhotoName(e.target.value)}
                placeholder="Your name"
                data-testid="photo-name-input"
                className="w-full h-11 rounded-2xl bg-secondary/60 border-0 px-4 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              />
              <input
                value={photoCaption}
                onChange={(e) => setPhotoCaption(e.target.value)}
                placeholder="Caption (optional)"
                data-testid="photo-caption-input"
                className="w-full h-11 rounded-2xl bg-secondary/60 border-0 px-4 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={uploading}
                data-testid="photo-upload-submit"
                className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-medium hover:bg-primary/90 transition-gentle disabled:opacity-60"
              >
                {uploading ? "Uploading\u2026" : "Add to the wall"}
              </button>
            </div>
          </form>
          {photos.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              {photos.map((p) => (
                <figure key={p.id} className="rounded-3xl overflow-hidden bg-card shadow-card transition-gentle hover:shadow-soft">
                  <img src={resolvePhoto(p.url)} alt={p.caption || person.fullName} className="h-44 w-full object-cover" loading="lazy" />
                  <figcaption className="p-3">
                    {p.caption && <div className="text-xs text-foreground">{p.caption}</div>}
                    <div className="text-[11px] text-muted-foreground">shared by {p.by}</div>
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </section>

        {/* Milestones (optional) */}
        {milestones.length > 0 && (
          <section>
            <div className="mb-6">
              <div className="text-xs uppercase tracking-[0.25em] text-primary">Calendar</div>
              <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-foreground">Milestones ahead</h2>
            </div>
            <div className="rounded-3xl bg-card shadow-card divide-y divide-border">
              {milestones.map((m) => (
                <div key={m.id} className="flex items-start gap-4 p-5">
                  <div className="shrink-0 text-center rounded-2xl bg-primary-soft px-3 py-2 min-w-[64px]">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-primary">
                      {new Date(m.date + "T00:00:00").toLocaleDateString("en-US", { month: "short" })}
                    </div>
                    <div className="font-serif text-xl text-primary leading-tight">
                      {new Date(m.date + "T00:00:00").getDate()}
                    </div>
                  </div>
                  <div>
                    <div className="font-serif text-base text-foreground">{m.title}</div>
                    <p className="text-xs text-muted-foreground mt-0.5">{m.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <ChurchSection person={person} churchMessages={data.churchMessages} onPost={handlePostChurchMessage} posting={posting} />
        <PageFooter slug={slug} toast={toast} />
      </div>
    </main>
  );
};

export default PersonPage;
